import { Alert, Button, Card, Descriptions, Select, Space, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Policy = {
  id: string;
  policy_no: string;
  title: string;
  category: string;
  summary: string;
  body_text?: string | null;
  effective_from: string;
  effective_to?: string | null;
  status: string;
  record_version: number;
};

export default function GovernancePolicyDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-governance-policy', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Policy>(`/education/governance/policies/${id}`)).data,
  });
  const row = query.data;

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/governance/policies/${id}/status`, {
        status,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('governancePolicyStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-governance-policy', id] });
      queryClient.invalidateQueries({ queryKey: ['education-governance-policies'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.policy_no || t('governancePolicies')}
      description={t('governancePoliciesIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/governance/policies')}>{t('governancePolicies')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('governanceSeparateHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('title')}>{row.title}</Descriptions.Item>
              <Descriptions.Item label={t('category')}>{row.category}</Descriptions.Item>
              <Descriptions.Item label={t('effectiveFrom')}>{row.effective_from}</Descriptions.Item>
              <Descriptions.Item label={t('effectiveTo')}>{row.effective_to || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {row.summary}
              </Descriptions.Item>
              <Descriptions.Item label={t('policyBody')} span={2}>
                {row.body_text || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        {canManage && row && row.status !== 'retired' ? (
          <Card size="small" title={t('status')}>
            <Select
              style={{ minWidth: 180 }}
              placeholder={t('status')}
              options={[
                ...(row.status === 'draft' ? [{ value: 'active', label: t('statusActive') }] : []),
                { value: 'retired', label: t('statusRetired') },
              ]}
              onChange={(v) => setStatus.mutate(v)}
            />
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
